import {
  Button,
  InputGroup,
  Label,
  Spinner,
  Surface,
  TextField,
} from "@heroui/react";

import { useAddressSearch } from "@/src/hooks/useAddressSearch";
import { useTranslations } from "@/src/i18n/i18n";
import type { GeocodingResult } from "@/src/libs/geocoding";

type AddressFieldProps = {
  /** Le lieu déjà choisi, à réafficher — vient de l'URL. */
  initialLabel?: string;
  onSelect: (_place: GeocodingResult) => void;
};

/**
 * « Lyon » plutôt que `45.764 / 4.836`.
 *
 * Partagé par les écrans qui cherchent autour d'un point — comptes et
 * événements. Il vit dans `components/` pour cette raison, et ses libellés dans
 * le scope `geo` plutôt que dans celui d'un écran.
 *
 * Le géocodage se fait **côté client**, contre Photon : moteur libre bâti sur
 * OpenStreetMap, sans clé d'API — c'est ce qui permet de ne rien attendre du
 * serveur pour cette fonctionnalité. Le module est celui de `wargame-app`, mot
 * pour mot ([geocoding.ts](src/libs/geocoding.ts)), pour que les deux
 * applications trouvent les mêmes adresses.
 *
 * ⚠️ **Un champ et une liste de boutons, pas un `ComboBox`.** Celui de HeroUI
 * est bâti sur `Select` : il attend une collection connue d'avance et filtre
 * lui-même, deux hypothèses fausses ici — les suggestions arrivent du réseau,
 * et « 69003 » doit pouvoir remonter « Lyon 3e Arrondissement », ce qu'aucune
 * comparaison de chaînes ne fait. Monté malgré tout, il ne rendait **aucun
 * champ de saisie**. La liste ci-dessous se parcourt à la tabulation et
 * s'active à l'entrée, et le nombre de résultats est annoncé.
 *
 * ⚠️ **La liste et les messages sont hors du flux** (`absolute`). Dans le flux,
 * ils allongeaient le bloc vers le bas — et comme la barre de filtres aligne
 * ses enfants par la base, l'apparition d'un « aucun lieu ne correspond »
 * remontait le champ au-dessus du rayon voisin. Une liste de suggestions doit
 * de toute façon survoler ce qui suit, pas le pousser.
 */
export function AddressField({ initialLabel, onSelect }: AddressFieldProps) {
  const t = useTranslations("geo");
  const { query, suggestions, isFetching, isEmpty, handleChangeText, commit } =
    useAddressSearch(initialLabel);

  return (
    <div className="relative w-full sm:w-80">
      <TextField value={query} onChange={handleChangeText}>
        <Label>{t("address")}</Label>
        <InputGroup>
          <InputGroup.Input placeholder={t("address-placeholder")} />
          {/* L'attente se voit : Photon met parfois une seconde, et un champ
              muet pendant ce temps se lit comme « aucun résultat ». */}
          {isFetching ? (
            <InputGroup.Suffix>
              <Spinner size="sm" />
            </InputGroup.Suffix>
          ) : null}
        </InputGroup>
      </TextField>

      {/* Ce que le champ ne peut pas montrer : combien de lieux sont proposés.
          `polite` — l'annonce attend une pause, elle ne coupe pas la frappe. */}
      <p aria-live="polite" className="sr-only">
        {suggestions.length > 0
          ? t("address-count", { count: suggestions.length })
          : ""}
      </p>

      {/* La liste est plus large que le champ, volontairement : « Place
          Charles Béraudier, Lyon, 69003, Auvergne-Rhône-Alpes, France » ne
          tient pas dans 20 rem, et c'est la fin du libellé — la ville, le pays
          — qui départage deux homonymes. Bornée à la fenêtre pour ne pas
          déborder sur mobile. */}
      {suggestions.length > 0 ? (
        <Surface className="absolute top-full left-0 z-30 mt-1 flex w-[28rem] max-w-[calc(100vw-3rem)] flex-col rounded-lg border border-separator p-1 shadow-lg">
          <ul>
            {suggestions.map((place) => (
              <li key={place.id}>
                <Button
                  // `whitespace-normal` : un `Button` ne passe pas à la ligne
                  // par défaut, et une adresse complète débordait de la liste.
                  className="h-auto justify-start py-1.5 text-left whitespace-normal"
                  fullWidth
                  size="sm"
                  variant="ghost"
                  onPress={() => {
                    // Le libellé choisi devient le texte du champ : il dit
                    // *quel* lieu a été retenu, là où des coordonnées seules
                    // ne se relisent pas.
                    commit(place.label);
                    onSelect(place);
                  }}
                >
                  {place.label}
                </Button>
              </li>
            ))}
          </ul>
        </Surface>
      ) : null}

      {/* `isEmpty` et non « pas de suggestions » : après un choix, la liste est
          vide elle aussi — et le lieu, lui, a bien été trouvé. */}
      {isEmpty ? (
        <p className="absolute top-full left-0 mt-1 text-xs text-muted">
          {t("address-empty")}
        </p>
      ) : null}
    </div>
  );
}
